/**
 * A special data texture that's used to mark object ids as highlighted.
 * @module
 */
import { INTEGER_ID_TRANSPARENT } from "common/constants/pickingConstants";
import { ClampToEdgeWrapping, DataTexture, NearestFilter, RGBAFormat, UnsignedByteType, UVMapping } from "three";

// These values control the relative intensity of the highlight color.
const PRESELECTED_PIXEL = 0x4c; // Preselected intensity = 76/255 = ~0.3
const SELECTED_PIXEL = 0x80; // Selected intensity = 128/255 = ~0.5
console.assert((PRESELECTED_PIXEL & SELECTED_PIXEL) === 0); // Bitmasks can't overlap, they need to be set and cleared independently from each other.

const ISOLATED_PIXEL_OFFSET = 3;

export const HIGHLIGHT_TEX_WIDTH = 256;

export enum HighlightColor {
  BLUE = 0,
  PURPLE = 1,
  // Note that adding more than 3 colors would require packing them into the texture in a different way, so just adding more values to this enum won't scale beyond that.
}

export class HighlightDataTexture {
  private data: Uint8Array<ArrayBuffer>;
  readonly texture: DataTexture;

  isolateMode: boolean = false;

  constructor() {
    this.data = new Uint8Array(HIGHLIGHT_TEX_WIDTH * HIGHLIGHT_TEX_WIDTH * 4);
    // The memory use could be optimized by packing the data more tightly, but this would complicate operating on it.
    this.texture = new DataTexture(
      this.data,
      HIGHLIGHT_TEX_WIDTH,
      HIGHLIGHT_TEX_WIDTH,
      RGBAFormat,
      UnsignedByteType,
      UVMapping,
      ClampToEdgeWrapping,
      ClampToEdgeWrapping,
      NearestFilter,
      NearestFilter,
    );
  }

  preselectId(id: number, highlightColor: HighlightColor) {
    if (id === INTEGER_ID_TRANSPARENT) {
      // Ideally this id should not end up in the selection state in the first place, but it's simplest to filter it out here.
      return;
    }
    const index = id * 4 + highlightColor;
    console.assert(index < this.data.length);
    if (this.data[index] & PRESELECTED_PIXEL) {
      return;
    }
    this.data[index] = this.data[index] | PRESELECTED_PIXEL;
    this.texture.needsUpdate = true;
  }

  depreselectId(id: number, highlightColor: HighlightColor) {
    if (id === INTEGER_ID_TRANSPARENT) {
      return;
    }
    const index = id * 4 + highlightColor;
    console.assert(index < this.data.length);
    if (this.data[index] & PRESELECTED_PIXEL) {
      this.data[index] = this.data[index] & ~PRESELECTED_PIXEL;
      this.texture.needsUpdate = true;
    }
  }

  selectId(id: number, highlightColor: HighlightColor) {
    if (id === INTEGER_ID_TRANSPARENT) {
      return;
    }
    const index = id * 4 + highlightColor;
    console.assert(index < this.data.length);
    if (this.data[index] & SELECTED_PIXEL) {
      return;
    }
    this.data[index] = this.data[index] | SELECTED_PIXEL;
    this.texture.needsUpdate = true;
  }

  deselectId(id: number, highlightColor: HighlightColor) {
    if (id === INTEGER_ID_TRANSPARENT) {
      return;
    }
    const index = id * 4 + highlightColor;
    console.assert(index < this.data.length);
    if (this.data[index] & SELECTED_PIXEL) {
      this.data[index] = this.data[index] & ~SELECTED_PIXEL;
      this.texture.needsUpdate = true;
    }
  }

  copySelectedToIsolated() {
    for (let id = 0; id < HIGHLIGHT_TEX_WIDTH * HIGHLIGHT_TEX_WIDTH; ++id) {
      const isolatedIndex = id * 4 + ISOLATED_PIXEL_OFFSET;
      this.data[isolatedIndex] =
        (this.data[id * 4] & SELECTED_PIXEL) !== 0 || (this.data[id * 4 + 1] & SELECTED_PIXEL) !== 0 ? 0xff : 0;
    }
    this.texture.needsUpdate = true;
  }

  clearIsolated() {
    for (let id = 0; id < HIGHLIGHT_TEX_WIDTH * HIGHLIGHT_TEX_WIDTH; ++id) {
      const isolatedIndex = id * 4 + ISOLATED_PIXEL_OFFSET;
      this.data[isolatedIndex] = 0;
    }
    this.texture.needsUpdate = true;
  }

  isolate(id: number) {
    const isolatedIndex = id * 4 + ISOLATED_PIXEL_OFFSET;
    console.assert(isolatedIndex < this.data.length);
    this.data[isolatedIndex] = 0xff;
    this.texture.needsUpdate = true;
  }

  deselectAll() {
    this.data.fill(0);
    this.texture.needsUpdate = true;
  }

  dispose() {
    this.texture.dispose();
  }
}
